<script>
   /*************************************************************/
   /* AppPlots component for training app                       */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';
   import { c, Vector } from 'mdatools/arrays';
   import { dnorm } from 'mdatools/distributions';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import local plot components
   import PlotFoM from '../plots/PlotFoM.svelte';
   import PlotDiscrimination from '../plots/PlotDiscrimination.svelte';
   import PlotROC from '../plots/PlotROC.svelte';
   import PlotFoMHeatmap from '../plots/PlotFoMHeatmap.svelte';
   import PlotScores from '../plots/PlotScores.svelte';
   import PlotDistance from '../plots/PlotDistance.svelte';

   // import external plot components
   import PlotRegFoM from '@mdatools-apps/shared/plots/PlotFoM.svelte';
   import PlotLoadings from '@mdatools-apps/shared/plots/PlotPLSLoadings.svelte';
   import PlotRegcoeffs from '@mdatools-apps/shared/plots/PlotRegcoeffs.svelte';
   import PlotVarsel from '@mdatools-apps/shared/plots/PlotPLSVarsel.svelte';
   import PlotPVScalars from '@mdatools-apps/shared/plots/PlotPVScalars.svelte';

   // import local plot controls components
   import { getPlotFoMControls, getPlotDiscriminationControls, getPlotDistanceControls,
      getPlotROCControls, getPlotFoMHeatmapControls, getPlotScoresControls,
   } from '../plots/Controls.svelte.js';

   // import external plot controls components
   import { getPlotPLSLoadingsControls, getPlotFoMControls as getPlotRegFoMControls,
      getPlotRegcoeffsControls, getPlotPVScalarsControls, getPlotPLSVarselControls,
   } from '@mdatools-apps/shared/plots/Controls.svelte.js';

   let {
      res,
      model,
      data,
      scalars,
      markOutlier,
      markRelabeled,
      excludeVars,
      showContributions,
      setModelParams,
      statMat
   } = $props();


   const resIDs = ['cal', 'pv'];
   const nresp = 1;
   const plsres = $derived({'cal': res.cal.pls, 'pv': res.pv.pls});
   const dares = $derived({'cal': res.cal.da, 'pv': res.pv.da});
   const ncomp = $derived(model.da.ncomp);


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      // objects
      pred: {label: 'Discrimination', scope: 'objects', component: PlotDiscrimination, buttons: buttons1},
      fom: {label: 'Figures of merit (plots)', scope: 'stats', component: PlotFoM},
      fomhm: {label: 'Figures of merit (heatmap)', scope: 'options', component: PlotFoMHeatmap, buttons: buttons3},
      roc: {label: 'ROC curve', scope: 'stats', component: PlotROC},

      // variables
      loadings: {label: 'X-loadings and weights', scope: 'stats', component: PlotLoadings},
      regcoeffs: {label: 'Regression coefficients', scope: 'stats', component: PlotRegcoeffs},
      varsel: {label: 'Variable importance', scope: 'variables', component: PlotVarsel, buttons: buttons2},

      // statistics
      fom2: {label: 'RMSE', scope: 'stats', component: PlotRegFoM},
      scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
      distance: {label: 'Distance', scope: 'objects', component: PlotDistance, buttons: buttons1},
      scalars: {label: 'PV scalars', scope: 'stats', component: PlotPVScalars}
   })

   // the controls are taken once, from the model and the results this component is mounted
   // with, and untrack marks that as deliberate: the tab keys this component on the model and
   // mounts it behind an '{#if}' on the results, which every reset nulls first, so a new model
   // or a new set arrives as a new component; the DA result alone is replaced in place, and the
   // one descriptor built from it reads its keys and nothing of its content.
   // the controls are a plain object: the descriptors are never changed after they are made,
   // so there is nothing here for $state to track
   const controls = untrack(() => ({
      pred: getPlotDiscriminationControls(resIDs, model.da.classNames),
      fom: getPlotFoMControls(dares, model.da.classNames),
      fomhm: getPlotFoMHeatmapControls(['cal', 'pv']),
      roc: getPlotROCControls(['cal', 'pv']),

      loadings: getPlotPLSLoadingsControls(model.pls),
      regcoeffs: getPlotRegcoeffsControls(nresp),
      varsel: getPlotPLSVarselControls(nresp),

      fom2: getPlotRegFoMControls(plsres, 1, ['rmse']),
      scores: getPlotScoresControls(plsres),
      distance: getPlotDistanceControls(plsres),
      scalars: getPlotPVScalarsControls(),
   }));

   // which controls do not apply to what the user has currently selected. the factories set
   // 'hidden' for what the data rules out, and Widget combines the two with OR. the outliers
   // zone is drawn under the soft rule only, and the rule is a model setting, not a plot
   // parameter: Widget runs the predicate in a derived while it renders, so the read of the
   // model in it is tracked like a read of the selection
   controls.pred.showOutliers.hiddenWhen = () => model.da.rule === 'hard';
   controls.loadings.comp1.hiddenWhen = (p) => p.plotType !== 'p';

   // JSON with currently selected plots parameters
   let params = $state({
      pred: getDefaults(controls.pred),
      fom: getDefaults(controls.fom),
      fomhm: getDefaults(controls.fomhm),
      roc: getDefaults(controls.roc),

      loadings: getDefaults(controls.loadings),
      regcoeffs: getDefaults(controls.regcoeffs),
      varsel: getDefaults(controls.varsel),

      fom2: getDefaults(controls.fom2),
      scores: getDefaults(controls.scores),
      distance: getDefaults(controls.distance),
      scalars: getDefaults(controls.scalars)
   });


   // combine main elements to be shared with all plots together
   const zscores = c(
      Vector.seq(-10.0, -3, 0.5),
      Vector.seq(-3, 3, 0.01),
      Vector.seq(3.0, 10, 0.5),
   );
   const densnorm = dnorm(zscores);
   const props = $derived({res:plsres, classres:dares, model:model.pls, classmodel: model.da, data, ncomp, scalars, zscores, densnorm, statMat});

   const name = $derived(model.da.rule === 'hard' ? 'tau' : 'alpha');
</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
      <Button onclick={() => markRelabeled(sel)} text="relabel" />
   </Container>
{/snippet}

{#snippet buttons2(sel)}
   <Container label="Selected">
      {#if (plsres.cal.npred - sel.length >= 2)}
      <Button onclick={() => excludeVars(sel)} class="destructive"
         text="exclude {sel.length} variable{sel.length === 1 ? '' : 's'}" />
      {:else}
      <p>can not exclude</p>
      {/if}
   </Container>
{/snippet}

{#snippet buttons3(opt)}
   {@const pstr = model.da.rule === 'hard' ? opt[1].toFixed(2) : opt[1].toFixed(0) + '%'}
   <Container labelWidth="auto" label="Selected: nLV = {opt[0]}, {name} = {pstr}">
      <Button onclick={() => setModelParams(opt)} text="Set" />
   </Container>
{/snippet}

<PlotsRouter
   {plots}
   {props}
   {controls}
   bind:params={params}
/>

<style>
   :global(.tab-content-train .plot-controls-area > .plot-selector-fom2),
   :global(.tab-content-train .plot-controls-area > .plot-selector-loadings) {
      margin-top: 1em;
   }
</style>